import {
    AuthorizationRequiredType,
    ClaimEligibilityItem,
    CoverageLevelType,
    EligibilityItemType,
    InPlanNetworkType,
    ListProviderClaimEligibilitiesRequest,
    PlanType,
    ProcedureCodeType,
    ProviderClaimEligibility,
    ProviderPayer,
    ProviderProvider,
    QuantityType,
    ServiceType,
    TimePeriodType,
} from '@enter/core';
import { format } from 'date-fns';
import { faker } from '@faker-js/faker';
import _ from 'lodash';
import { ClaimGenerator } from './claim.generator';
import { IEligibilityListing } from './eligibility.store';
import { waitForNoReason } from './util';
import { generateAmount, oneOf, upToNTimes } from './util.generator';

export class EligibilityGenerator {
    public eligibilities: ProviderClaimEligibility[] = [];
    public payers: ProviderPayer[];
    public providers: ProviderProvider[];

    // TODO: extract index/storeTo/loadFrom as utility class
    public index: Map<string, Map<string, string[]>> = new Map<string, Map<string, string[]>>();

    constructor(private readonly claimGenerator: ClaimGenerator) {
        this.generateEntities(5);
        this.payers = claimGenerator.payers;
        this.providers = claimGenerator.providers;
    }

    public async generateEligibilityListing(
        lpcr: ListProviderClaimEligibilitiesRequest,
    ): Promise<IEligibilityListing | undefined> {
        const eligibilities = this.eligibilities;
        await waitForNoReason(400);
        return {
            eligibilities,
            limit: lpcr.limit || 20,
            offset: lpcr.offset || 0,
            total: eligibilities.length,
        };
    }

    private generateEntities(count: number = 3): void {
        // generate eligibilities
        _.times(count, n => this.generateProviderClaimEligibility(faker.string.uuid()));
    }

    private generateProviderClaimEligibility(date: string): ProviderClaimEligibility {
        return {
            created: date,
            id: faker.string.uuid(),
            planId: String(_.random(1e6, 1e9)),
            planDescription: faker.lorem.sentence(),
            groupId: String(_.random(1e6, 1e9)),
            groupDescription: faker.lorem.sentence(),
            otherQualifierId: String(_.random(1e6, 1e9)),
            otherId: String(_.random(1e6, 1e9)),
            otherDescription: faker.lorem.sentence(),
            planStartDate: date,
            planEndDate: date,
            items: upToNTimes(() => this.generateEligibilityItem(), 5),
        };
    }

    private generateEligibilityItem = (): ClaimEligibilityItem => {
        const from = faker.date.recent();
        return {
            type: oneOf(EligibilityItemType),
            coverageLevelType: oneOf(CoverageLevelType),
            planType: oneOf(PlanType),
            coverageDescription: faker.lorem.sentence(),
            timePeriodType: oneOf(TimePeriodType),
            benefitAmount: generateAmount(),
            benefitPercentage: `${_.random(1, 50)}%`,
            quantityType: oneOf(QuantityType),
            quantity: String(_.random(1, 4)),
            authorizationRequiredType: oneOf(AuthorizationRequiredType),
            inPlanNetworkType: oneOf(InPlanNetworkType),
            procedureCodeType: oneOf(ProcedureCodeType),
            fromProcedureCode: faker.lorem.word(),
            throughProcedureCode: faker.lorem.word(),
            modifierCode1: faker.lorem.word(),
            modifierCode2: faker.lorem.word(),
            modifierCode3: faker.lorem.word(),
            modifierCode4: faker.lorem.word(),
            diagnosisPointer1: faker.lorem.word(),
            diagnosisPointer2: faker.lorem.word(),
            diagnosisPointer3: faker.lorem.word(),
            diagnosisPointer4: faker.lorem.word(),
            eligibilityFromDate: format(from, 'yyyy-MM-dd'),
            eligibilityThroughDate: this.claimGenerator.recentDateAfter(from),
            serviceTypeCodes: upToNTimes(() => oneOf(ServiceType), 4),
            messages: upToNTimes(() => faker.lorem.sentence(), 1, 3),
        };
    };

    private loadFromIndex<T>(model: string, key: string): T[] | undefined {
        const array = _.get(this, model);
        const index = this.index.get(model);
        if (array && index) {
            const ids = index.get(key);
            return _.filter(array, (item: T): boolean => _.includes(ids, _.get(item, 'id')));
        }
    }
}
